// =============================================================================
// SupplierMoneyCard — T-385. The AR-per-client mirror for the money we OWE:
// per-supplier owing / overdue ("are we late?") / open PO commitments /
// paid-to-date, plus a 12-month spend sparkline. Mounted on the supplier
// detail AND the subbie cockpit (via its linked supplier) so compliance and
// money sit side by side.
// =============================================================================

import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Banknote, AlertTriangle } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Card, Spinner } from "@/components/ui";
import { fmtMoney, fmtDate } from "@/lib/dates";

export default function SupplierMoneyCard({ supplierId }: { supplierId: string }) {
  const { data: sum, isLoading } = useQuery({
    queryKey: ["supplier_ap_summary", supplierId],
    queryFn: async () => {
      const { data } = await supabase
        .from("v_supplier_ap_summary")
        .select("*")
        .eq("supplier_id", supplierId)
        .maybeSingle();
      return data as any;
    },
  });

  // 12-month spend history (entered + paid — i.e. real billed amounts).
  const { data: monthly = [] } = useQuery({
    queryKey: ["supplier_ap_monthly", supplierId],
    queryFn: async () => {
      const from = new Date();
      from.setMonth(from.getMonth() - 11); from.setDate(1);
      const { data } = await supabase
        .from("ap_invoices")
        .select("invoice_date, amount_inc_gst, status")
        .eq("supplier_id", supplierId)
        .gte("invoice_date", from.toISOString().slice(0, 10))
        .not("status", "in", "(void,disputed)");
      const buckets = new Map<string, number>();
      for (let i = 0; i < 12; i++) {
        const d = new Date(from); d.setMonth(from.getMonth() + i);
        buckets.set(d.toISOString().slice(0, 7), 0);
      }
      for (const r of (data ?? []) as any[]) {
        const k = String(r.invoice_date ?? "").slice(0, 7);
        if (buckets.has(k)) buckets.set(k, (buckets.get(k) ?? 0) + Number(r.amount_inc_gst ?? 0));
      }
      return [...buckets.entries()].map(([month, amt]) => ({ month, amt }));
    },
  });

  const maxAmt = useMemo(() => Math.max(1, ...monthly.map(m => m.amt)), [monthly]);

  if (isLoading) return <Card className="mb-4"><div className="p-6 flex justify-center"><Spinner size={16} /></div></Card>;
  if (!sum) return null;

  const late = Number(sum.overdue_count) > 0;

  return (
    <Card className={`mb-4 ${late ? "border-danger/40" : ""}`}>
      <div className="p-4">
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold flex items-center gap-1.5">
            <Banknote size={13} className="text-accent" /> Money
          </div>
          {late ? (
            <span className="text-[11px] px-2 py-0.5 rounded bg-danger/15 text-danger border border-danger/40 font-medium inline-flex items-center gap-1">
              <AlertTriangle size={11} /> we're late — oldest overdue {sum.oldest_overdue_days}d ({fmtDate(sum.oldest_overdue_due)})
            </span>
          ) : Number(sum.bills_owing) > 0 ? (
            <span className="text-[11px] text-text-muted">nothing overdue</span>
          ) : (
            <span className="text-[11px] text-ok">all square</span>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[13px] mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-text-muted">Owing</div>
            <Link to="/ap/bills" className="font-mono text-[15px] text-text hover:text-accent">
              {fmtMoney(Number(sum.owing_inc_gst))}
            </Link>
            <div className="text-[10px] text-text-muted">{sum.bills_owing} bill{Number(sum.bills_owing) === 1 ? "" : "s"}{late ? ` · ${sum.overdue_count} overdue (${fmtMoney(Number(sum.overdue_inc_gst))})` : ""}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-text-muted">Open PO commitments</div>
            <Link to="/pos" className="font-mono text-[15px] text-text hover:text-accent">{fmtMoney(Number(sum.po_committed_ex_gst))}</Link>
            <div className="text-[10px] text-text-muted">ex GST · not yet invoiced</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-text-muted">Paid this FY</div>
            <div className="font-mono text-[15px] text-text">{fmtMoney(Number(sum.paid_fy))}</div>
            <div className="text-[10px] text-text-muted">{sum.last_paid_date ? `last paid ${fmtDate(sum.last_paid_date)}` : "—"}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-text-muted">All time</div>
            <div className="font-mono text-[15px] text-text">{fmtMoney(Number(sum.paid_all_time))}</div>
            <div className="text-[10px] text-text-muted">{sum.invoice_count} invoices</div>
          </div>
        </div>

        {/* 12-month billed sparkline */}
        <div className="flex items-end gap-1 h-12" title="Billed per month, last 12 months (inc GST)">
          {monthly.map(m => (
            <div key={m.month} className="flex-1 flex flex-col items-center gap-0.5" title={`${m.month}: ${fmtMoney(m.amt)}`}>
              <div
                className={`w-full rounded-sm ${m.amt > 0 ? "bg-accent/70" : "bg-ink-raised"}`}
                style={{ height: `${Math.max(m.amt > 0 ? 8 : 3, Math.round((m.amt / maxAmt) * 40))}px` }}
              />
            </div>
          ))}
        </div>
        <div className="flex justify-between text-[9px] text-text-muted font-mono mt-0.5">
          <span>{monthly[0]?.month ?? ""}</span>
          <span>{monthly[monthly.length - 1]?.month ?? ""}</span>
        </div>
      </div>
    </Card>
  );
}
